import { useState } from 'react';
import { RefreshCw, Activity } from 'lucide-react';
import { facebookAdAccountService, AdAccountHealth } from '@/services/facebook-ad-account.service';
import { metaAccountStatusLabel } from '@/utils/currency';
import toast from 'react-hot-toast';

interface Props { initial: AdAccountHealth | null; onRefresh: (h: AdAccountHealth) => void; }

const colorClass: Record<string, string> = {
  success: 'bg-success/15 text-success',
  warning: 'bg-warning/15 text-warning',
  danger: 'bg-danger/15 text-danger',
  muted: 'bg-surface-alt text-text-muted',
};

export const AdHealthCard: React.FC<Props> = ({ initial, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    setBusy(true);
    try { const r = await facebookAdAccountService.health(true); onRefresh(r); if (r.verified) toast.success('Health refreshed'); else toast.error(r.error || 'Could not verify account health'); }
    catch { /* toast shown */ } finally { setBusy(false); }
  };
  const status = initial?.verified === false ? { color: 'warning', label: 'Unverified' } : metaAccountStatusLabel(initial?.accountStatus);
  const usage = (initial?.rateLimitUsage as any) ?? {};

  // Try to extract a friendly call_count / total_time number from the rate-limit usage
  let rateLimitText = 'Unknown';
  if (usage.call_count !== undefined) {
    rateLimitText = `${usage.call_count}% calls · ${usage.total_time ?? 0}% time · ${usage.total_cputime ?? 0}% CPU`;
  } else if (usage.raw) {
    rateLimitText = String(usage.raw).slice(0, 60);
  }

  return (
    <div className="bg-surface border border-border rounded-lg p-5">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-primary" />
          <h3 className="text-sm font-semibold text-text-primary">Account Health</h3>
        </div>
        <button onClick={refresh} disabled={busy} className="p-1.5 rounded-md hover:bg-surface-alt text-text-muted disabled:opacity-50">
          <RefreshCw aria-label="Refresh account health" size={14} className={busy ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-xs text-text-muted">Status</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colorClass[status.color]}`}>{status.label}</span>
        </div>
        {initial?.disableReason !== undefined && initial.disableReason !== 0 && (
          <div className="text-xs text-danger">Disable reason code: {initial.disableReason}</div>
        )}
        <div className="flex justify-between items-center">
          <span className="text-xs text-text-muted">Account age</span>
          <span className="text-xs font-mono">{initial?.age ? `${initial.age} days` : '—'}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-text-muted">Timezone</span>
          <span className="text-xs font-mono">{initial?.timezoneName ?? '—'}</span>
        </div>
        <div>
          <div className="text-xs text-text-muted mb-1">API Rate Limit Usage</div>
          <div className="text-xs font-mono">{rateLimitText}</div>
        </div>
        {initial?.error && <p role="status" className="text-xs text-warning">{initial.error}</p>}
        {initial?.staleSince && (
          <div className="text-[10px] text-warning">⚠ Stale — last fetched {new Date(initial.staleSince).toLocaleString()}</div>
        )}
      </div>
    </div>
  );
};
